import { z } from 'zod'
import { segmentSchema } from './clients.js'
import { metricValueSchema } from './metrics.js'
import { periodoSchema, provenanceSchema } from './primitives.js'

/**
 * Módulo Revenue — solo Admin. Todo lo de este archivo es $$: la API no lo
 * sirve a ops ni a comercial, y la ruta entera está detrás de requireRole.
 */

/**
 * Fila de la tabla mensual editable. Reemplazo directo de la hoja "Revenue".
 * Los derivados (netNewMrr, growthMoMPct) vienen calculados en lectura con las
 * funciones de `derived.ts`: no se almacenan.
 */
export const revenueMonthSchema = z.object({
  periodo: periodoSchema,
  /** null = mes sin dato cargado. Nunca cero por defecto. */
  mrr: z.number().nullable(),
  clientesNuevos: z.number().nullable(),
  clientesChurned: z.number().nullable(),
  nuevoMrr: z.number().nullable(),
  expansionMrr: z.number().nullable(),
  churnedMrr: z.number().nullable(),
  /** Derivado: nuevoMrr + expansionMrr − churnedMrr. */
  netNewMrr: z.number().nullable(),
  /** Derivado: crecimiento contra el mes anterior, en %. */
  growthMoMPct: z.number().nullable(),
  /** Base del delta, para poder recalcularlo a mano. */
  mrrBase: z.number().nullable(),
  /** 'YYYY-MM' del mes contra el que se comparó. null en el primer mes. */
  periodoBase: z.string().nullable(),
  provenance: provenanceSchema,
})
export type RevenueMonth = z.infer<typeof revenueMonthSchema>

/** Carga/edición manual de un mes. Lo que acepta el endpoint de ingesta. */
export const upsertRevenueMonthSchema = z.object({
  periodo: periodoSchema,
  mrr: z.number().nonnegative().nullable(),
  clientesNuevos: z.number().int().nonnegative().nullable(),
  clientesChurned: z.number().int().nonnegative().nullable(),
  nuevoMrr: z.number().nullable(),
  expansionMrr: z.number().nullable(),
  churnedMrr: z.number().nullable(),
  nota: z.string().max(500).optional(),
})
export type UpsertRevenueMonthInput = z.infer<typeof upsertRevenueMonthSchema>

/** Split Business (self-serve) vs Enterprise. */
export const segmentRevenueSchema = z.object({
  segmento: segmentSchema,
  periodo: periodoSchema,
  mrr: z.number().nullable(),
  clientes: z.number(),
  /** Peso del segmento sobre el MRR total, en %. null si no hay MRR total. */
  pctMrr: z.number().nullable(),
  /** MRR / clientes. null si no hay clientes. */
  arpu: z.number().nullable(),
  provenance: provenanceSchema,
})
export type SegmentRevenue = z.infer<typeof segmentRevenueSchema>

/**
 * Panel de unit economics. Cada tarjeta es un MetricValue porque así trae su
 * fórmula, su benchmark, su semáforo y su proveniencia sin duplicar tipos.
 * `bases` publica los insumos para que cualquier número sea recalculable a mano.
 */
export const unitEconomicsSchema = z.object({
  periodo: periodoSchema,
  /** Tarjetas: arpu, cac, ltv, ltv_cac, payback, nrr, churn_mensual, churn_anual, gross_margin. */
  metricas: z.array(metricValueSchema),
  bases: z.object({
    arpu: z.number().nullable(),
    mrrPorCliente: z.number().nullable(),
    clientesActivos: z.number().nullable(),
    churnMensualPct: z.number().nullable(),
    cac: z.number().nullable(),
    /** Slugs que hoy no tienen dato y bloquean los derivados. */
    faltantes: z.array(z.string()),
  }),
})
export type UnitEconomics = z.infer<typeof unitEconomicsSchema>

/** Suscripción de Stripe asociada a un cliente. Deep link a Stripe. */
export const subscriptionRefSchema = z.object({
  clientId: z.string(),
  clientNombre: z.string(),
  stripeCustomerId: z.string().nullable(),
  stripeSubscriptionId: z.string().nullable(),
  mrr: z.number().nullable(),
  segmento: segmentSchema,
})
export type SubscriptionRef = z.infer<typeof subscriptionRefSchema>

export const revenuePayloadSchema = z.object({
  periodo: periodoSchema,
  /** 12 meses, del más viejo al más nuevo. */
  meses: z.array(revenueMonthSchema),
  porSegmento: z.array(segmentRevenueSchema),
  unitEconomics: unitEconomicsSchema,
  suscripciones: z.array(subscriptionRefSchema),
})
export type RevenuePayload = z.infer<typeof revenuePayloadSchema>
